/**
 * Pure (viewer-independent) wall-region partitioner.
 *
 * Extracts the per-element partitioning logic from
 * `PointoramaWallSketch.computeWallRegions` into a free function with no `this`,
 * no viewer, no Three.js renderer state. Same algorithm: at each node, every
 * incident wall tests its two offset lines against its two angular neighbours;
 * caps land on those intersections (or on a perpendicular divider for
 * near-collinear neighbours). Walls become quads sharing the cap corners with
 * the junction cells, so the partition TILES with no gap.
 *
 * Output regions match the original method's output exactly, including
 * close-node merge resolution. Decoupled here so unit tests can lock the
 * partitioner's geometry without spinning up a viewer.
 */

import * as THREE from 'three';
import type { SkeletonNodeData, SkeletonEdgeData } from '@pointorama/potree-shared';
import type { WallRegion } from './stitchedWallMesh';

/** Per-edge cached offset-line data. */
export interface EdgeOffsetData {
  edge: SkeletonEdgeData;
  startNode: SkeletonNodeData;
  endNode: SkeletonNodeData;
  direction: THREE.Vector2;
  perpLeft: THREE.Vector2;
  leftSize: number;
  rightSize: number;
  leftStart: THREE.Vector2;
  leftEnd: THREE.Vector2;
  rightStart: THREE.Vector2;
  rightEnd: THREE.Vector2;
}

/** Pure: build a single edge's offset lines from raw nodes/edge data. */
export function computeEdgeOffsets(edge: SkeletonEdgeData, startNode: SkeletonNodeData, endNode: SkeletonNodeData): EdgeOffsetData {
  const start = new THREE.Vector2(startNode.position.x, startNode.position.y);
  const end = new THREE.Vector2(endNode.position.x, endNode.position.y);
  const direction = new THREE.Vector2().subVectors(end, start).normalize();
  const perpLeft = new THREE.Vector2(-direction.y, direction.x);
  const leftSize = edge.leftSize || 0.1;
  const rightSize = edge.rightSize || 0.1;
  return {
    edge,
    startNode,
    endNode,
    direction,
    perpLeft,
    leftSize,
    rightSize,
    leftStart: start.clone().add(perpLeft.clone().multiplyScalar(leftSize)),
    leftEnd: end.clone().add(perpLeft.clone().multiplyScalar(leftSize)),
    rightStart: start.clone().add(perpLeft.clone().multiplyScalar(-rightSize)),
    rightEnd: end.clone().add(perpLeft.clone().multiplyScalar(-rightSize)),
  };
}

function lineLineIntersection(p1: THREE.Vector2, p2: THREE.Vector2, p3: THREE.Vector2, p4: THREE.Vector2): THREE.Vector2 | null {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 0.0001) return null;
  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;
  const t = (dx * d2y - dy * d2x) / cross;
  return new THREE.Vector2(p1.x + t * d1x, p1.y + t * d1y);
}

const COLLINEAR_THRESHOLD = 0.95;

export interface ResolveBoundsFn {
  (constraint: SkeletonNodeData['height'] | SkeletonEdgeData['height'], floorHeight: number): { bottom: number; top: number };
}

/**
 * The default bounds resolver used by tests and any caller that doesn't need
 * per-element height constraints — bottom = floorHeight, top = floorHeight + 1.
 */
export const defaultResolveBounds: ResolveBoundsFn = (_constraint, floorHeight) => ({ bottom: floorHeight, top: floorHeight + 1 });

/**
 * Build node→edgeIds adjacency from a list of edges. Useful when constructing
 * inputs to `computeWallRegions` directly (e.g. tests).
 */
export function buildNodeEdgeIds(edges: SkeletonEdgeData[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const e of edges) {
    const a = m.get(e.startNodeId) ?? [];
    a.push(e.id);
    m.set(e.startNodeId, a);
    const b = m.get(e.endNodeId) ?? [];
    b.push(e.id);
    m.set(e.endNodeId, b);
  }
  return m;
}

/**
 * Partition the wall footprint into per-element regions using the
 * first-intersection model. See `PointoramaWallSketch.computeWallRegions` (the
 * docblock there) for the algorithm.
 *
 * `resolveBounds` resolves a per-element height constraint to a `{bottom, top}`
 * pair — the class method threads through floor-group context; tests can pass
 * `defaultResolveBounds`.
 */
export function computeWallRegions(
  edges: SkeletonEdgeData[],
  nodeMap: Map<string, SkeletonNodeData>,
  edgeOffsets: Map<string, EdgeOffsetData>,
  nodeEdgeIds: Map<string, string[]>,
  floorHeight: number,
  resolveBounds: ResolveBoundsFn = defaultResolveBounds,
): WallRegion[] {
  const regions: WallRegion[] = [];

  type Side = { p1: THREE.Vector2; p2: THREE.Vector2 };
  const X = (a: Side, b: Side) => lineLineIntersection(a.p1, a.p2, b.p1, b.p2);

  const corner = new Map<string, THREE.Vector2>();
  const cells = new Map<string, THREE.Vector2[]>();

  for (const [nodeId, eIds] of nodeEdgeIds) {
    const node = nodeMap.get(nodeId);
    if (!node) continue;
    const np = new THREE.Vector2(node.position.x, node.position.y);

    const arr = eIds
      .map((eId) => {
        const od = edgeOffsets.get(eId);
        if (!od) return null;
        const isStart = od.startNode.id === nodeId;
        const dir = isStart ? od.direction.clone() : od.direction.clone().negate();
        const leftLine: Side = isStart ? { p1: od.leftStart, p2: od.leftEnd } : { p1: od.rightEnd, p2: od.rightStart };
        const rightLine: Side = isStart ? { p1: od.rightStart, p2: od.rightEnd } : { p1: od.leftEnd, p2: od.leftStart };
        return { eId, dir, leftLine, rightLine, angle: Math.atan2(dir.y, dir.x) };
      })
      .filter((x): x is { eId: string; dir: THREE.Vector2; leftLine: Side; rightLine: Side; angle: number } => x !== null)
      .sort((a, b) => a.angle - b.angle);
    const m = arr.length;
    if (m === 0) continue;

    if (m === 1) {
      const e = arr[0];
      corner.set(`${nodeId}|${e.eId}|L`, e.leftLine.p1.clone());
      corner.set(`${nodeId}|${e.eId}|R`, e.rightLine.p1.clone());
      continue;
    }

    const CAP_MAX_DIST = 0.5;
    const resolveCap = (own: Side, nbr: Side, perp: Side, dirDot: number): { pt: THREE.Vector2; divider: boolean } => {
      if (dirDot > -COLLINEAR_THRESHOLD) {
        const hit = X(own, nbr);
        if (hit && hit.distanceTo(np) <= CAP_MAX_DIST) return { pt: hit, divider: false };
      }
      return { pt: X(own, perp) ?? own.p1.clone(), divider: true };
    };

    const collinear: Array<{ L: boolean; R: boolean }> = [];
    for (let i = 0; i < m; i++) {
      const e = arr[i];
      const ccw = arr[(i + 1) % m];
      const cw = arr[(i - 1 + m) % m];
      const perp: Side = { p1: np, p2: new THREE.Vector2(np.x - e.dir.y, np.y + e.dir.x) };
      const left = resolveCap(e.leftLine, ccw.rightLine, perp, e.dir.dot(ccw.dir));
      const right = resolveCap(e.rightLine, cw.leftLine, perp, e.dir.dot(cw.dir));
      collinear.push({ L: left.divider, R: right.divider });
      corner.set(`${nodeId}|${e.eId}|L`, left.pt);
      corner.set(`${nodeId}|${e.eId}|R`, right.pt);
    }

    const isJunction = collinear.some((c) => !c.L || !c.R);
    if (isJunction) {
      const ring: THREE.Vector2[] = [];
      for (const e of arr) {
        const cr = corner.get(`${nodeId}|${e.eId}|R`);
        const cl = corner.get(`${nodeId}|${e.eId}|L`);
        if (cr) ring.push(cr);
        if (cl) ring.push(cl);
      }
      const clean: THREE.Vector2[] = [];
      for (let i = 0; i < ring.length; i++) {
        const p = ring[i];
        const q = ring[(i + 1) % ring.length];
        if (p.distanceToSquared(q) > 1e-14) clean.push(p);
      }
      if (clean.length >= 3) cells.set(nodeId, clean);
    }
  }

  const physLeft = (od: EdgeOffsetData, nodeId: string, eId: string) => (od.startNode.id === nodeId ? corner.get(`${nodeId}|${eId}|L`) : corner.get(`${nodeId}|${eId}|R`));
  const physRight = (od: EdgeOffsetData, nodeId: string, eId: string) => (od.startNode.id === nodeId ? corner.get(`${nodeId}|${eId}|R`) : corner.get(`${nodeId}|${eId}|L`));

  const projOnLine = (q: THREE.Vector2, p1: THREE.Vector2, p2: THREE.Vector2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dd = dx * dx + dy * dy || 1;
    const t = ((q.x - p1.x) * dx + (q.y - p1.y) * dy) / dd;
    return new THREE.Vector2(p1.x + t * dx, p1.y + t * dy);
  };
  // Re-emit a fresh, non-shared corner for THIS edge at this node + side. The
  // close-node merge must move only the connector's own caps; if it mutated the
  // shared corner object, the adjacent long walls' caps (which sit at the same
  // intersection point) would be pulled along, crooking those walls. So we
  // replace the connector's corner with a clone first, then mutate it.
  const writeOwnCorner = (nodeId: string, eId: string, side: 'L' | 'R', to: THREE.Vector2) => {
    const fresh = new THREE.Vector2(to.x, to.y);
    corner.set(`${nodeId}|${eId}|${side}`, fresh);
  };
  const EPS = 1e-6;
  for (const edge of edges) {
    const od = edgeOffsets.get(edge.id);
    if (!od) continue;
    const dir = od.direction;
    const mid = new THREE.Vector2((od.startNode.position.x + od.endNode.position.x) / 2, (od.startNode.position.y + od.endNode.position.y) / 2);
    // Physical-side → outgoing-frame side name at a given node. Mirrors
    // physLeft/physRight: at the start node L=L, R=R; at the end node L=R, R=L.
    const sideKeyAt = (nodeId: string, physical: 'L' | 'R'): 'L' | 'R' => {
      if (nodeId === od.startNode.id) return physical;
      return physical === 'L' ? 'R' : 'L';
    };
    const sides: Array<{ cs?: THREE.Vector2; ce?: THREE.Vector2; p1: THREE.Vector2; p2: THREE.Vector2; physical: 'L' | 'R' }> = [
      { cs: physLeft(od, od.startNode.id, edge.id), ce: physLeft(od, od.endNode.id, edge.id), p1: od.leftStart, p2: od.leftEnd, physical: 'L' },
      { cs: physRight(od, od.startNode.id, edge.id), ce: physRight(od, od.endNode.id, edge.id), p1: od.rightStart, p2: od.rightEnd, physical: 'R' },
    ];
    const axial = (p: THREE.Vector2) => (p.x - od.startNode.position.x) * dir.x + (p.y - od.startNode.position.y) * dir.y;
    for (const s of sides) {
      if (!s.cs || !s.ce) continue;
      if (axial(s.cs) <= axial(s.ce) + EPS) continue;
      // Caps crossed on this side. Replace BOTH of THIS EDGE'S caps with
      // fresh copies at the wall midpoint (projected onto this side's
      // offset line). The neighbouring walls' caps at the same node — even
      // if they started at the SAME shared position — keep their original
      // object references and therefore their original positions, so
      // adjacent long walls stay straight.
      const m = projOnLine(mid, s.p1, s.p2);
      writeOwnCorner(od.startNode.id, edge.id, sideKeyAt(od.startNode.id, s.physical), m);
      writeOwnCorner(od.endNode.id, edge.id, sideKeyAt(od.endNode.id, s.physical), m);
    }
  }

  for (const edge of edges) {
    const od = edgeOffsets.get(edge.id);
    if (!od) continue;
    const sl = physLeft(od, od.startNode.id, edge.id) ?? od.leftStart;
    const sr = physRight(od, od.startNode.id, edge.id) ?? od.rightStart;
    const el = physLeft(od, od.endNode.id, edge.id) ?? od.leftEnd;
    const er = physRight(od, od.endNode.id, edge.id) ?? od.rightEnd;
    const bounds = resolveBounds(edge.height, floorHeight);
    regions.push({ ring: [sl, el, er, sr], bottom: bounds.bottom, top: bounds.top });
  }

  for (const [nodeId] of nodeEdgeIds) {
    const node = nodeMap.get(nodeId);
    const cell = cells.get(nodeId);
    if (!node || !cell) continue;
    const bounds = resolveBounds(node.height, floorHeight);
    regions.push({ ring: cell, bottom: bounds.bottom, top: bounds.top, isNodeCell: true });
  }

  return regions;
}
